import type { CacheListener, IReactiveCache } from './IIndexedDbCache'

interface IndexedDbCacheProps<T> {
  dbName: string
  storeName: string
  keyPath: keyof T & string
  version: number
}

/**
 * Generic reactive cache backed by IndexedDB.
 *
 * Keeps an in-memory snapshot with stable references (a record reference and
 * per-key references change only when that entry is written), so it can serve
 * `useSyncExternalStore` directly. Degrades to a no-op when IndexedDB is
 * unavailable (SSR, private mode, tests without a fake implementation).
 *
 * The browser may close the connection on its own (tab freeze, bfcache, evicted
 * storage), so writes never throw: the cache drops the stale handle, reopens and
 * rewrites the snapshot.
 */
class IndexedDbCache<T> implements IReactiveCache<T> {
  private db?: IDBDatabase
  private isOpening = false
  private isRecreated = false
  private snapshot: Record<string, T> = {}
  private entriesCount = 0
  private readonly ready: Promise<void>
  private resolveReady!: () => void
  private readonly globalListeners = new Set<CacheListener>()
  private readonly keyListeners = new Map<string, Set<CacheListener>>()
  private readonly available = typeof indexedDB !== 'undefined'
  private readonly dbName: string
  private readonly storeName: string
  private readonly keyPath: keyof T & string
  private readonly version: number

  constructor({ dbName, storeName, keyPath, version }: IndexedDbCacheProps<T>) {
    this.dbName = dbName
    this.storeName = storeName
    this.keyPath = keyPath
    this.version = version
    this.ready = new Promise(resolve => {
      this.resolveReady = resolve
    })
    this.open()
  }

  whenReady() {
    return this.ready
  }

  getAll(): Readonly<Record<string, T>> {
    return this.snapshot
  }

  getSnapshot(key: string): T | undefined {
    return this.snapshot[key]
  }

  hasEntries(): boolean {
    return this.entriesCount > 0
  }

  subscribe(listener: CacheListener) {
    this.globalListeners.add(listener)
    return () => {
      this.globalListeners.delete(listener)
    }
  }

  subscribeKey(key: string, listener: CacheListener) {
    let listeners = this.keyListeners.get(key)
    if (!listeners) {
      listeners = new Set()
      this.keyListeners.set(key, listeners)
    }
    listeners.add(listener)
    return () => {
      listeners?.delete(listener)
      if (listeners?.size === 0) {
        this.keyListeners.delete(key)
      }
    }
  }

  put(value: T) {
    this.putMany([value])
  }

  putMany(values: T[]) {
    if (values.length === 0) {
      return
    }
    const next = { ...this.snapshot }
    values.forEach(value => {
      const key = value[this.keyPath] as string
      if (!(key in next)) {
        this.entriesCount++
      }
      next[key] = value
    })
    this.snapshot = next

    const store = this.getStore(true)
    values.forEach(value => store?.put(value))

    values.forEach(value => this.notifyKey(value[this.keyPath] as string))
    this.notifyGlobal()
  }

  remove(keys: string[]) {
    if (keys.length === 0) {
      return
    }
    const next = { ...this.snapshot }
    const removed: string[] = []
    keys.forEach(key => {
      if (key in next) {
        delete next[key]
        this.entriesCount--
        removed.push(key)
      }
    })
    if (removed.length === 0) {
      return
    }
    this.snapshot = next

    const store = this.getStore(true)
    removed.forEach(key => store?.delete(key))

    removed.forEach(key => this.notifyKey(key))
    this.notifyGlobal()
  }

  clear() {
    this.snapshot = {}
    this.entriesCount = 0
    this.getStore(true)?.clear()
    this.keyListeners.forEach((_, key) => this.notifyKey(key))
    this.notifyGlobal()
  }

  private open(restoreSnapshot = false) {
    if (!this.available) {
      this.resolveReady()
      return
    }
    if (this.isOpening) {
      return
    }
    this.isOpening = true
    const request = indexedDB.open(this.dbName, this.version)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(this.storeName)) {
        db.createObjectStore(this.storeName, { keyPath: this.keyPath })
      }
    }
    request.onsuccess = () => {
      const db = request.result
      this.isOpening = false
      this.db = db
      // The browser dropped the connection on its own, so the in-memory state is
      // the only surviving copy — put it back.
      db.onclose = () => this.reopen(true)
      db.onversionchange = event => {
        db.close()
        this.db = undefined
        if (event.newVersion !== null) {
          // Another tab upgraded the schema; reopening at our version would fail
          // with a VersionError, so stay closed and serve reads from memory.
          return
        }
        // The database was deleted on purpose (devtools, "clear site data").
        // Reopen it empty instead of resurrecting what was just thrown away.
        this.open()
      }
      if (restoreSnapshot) {
        this.flushSnapshot()
        return
      }
      this.load()
    }
    request.onerror = () => {
      this.isOpening = false
      // A database left behind by a newer build — a branch switch, or a rolled
      // back release — cannot be opened at our version, and every read and write
      // would silently turn into a no-op. Nothing here cannot be refetched, so
      // drop the file and start over instead of staying dead until the user
      // clears the storage by hand.
      if (request.error?.name === 'VersionError' && !this.isRecreated) {
        this.isRecreated = true
        this.recreate(restoreSnapshot)
        return
      }
      console.error(`IndexedDbCache: cannot open ${this.dbName}`, request.error) // eslint-disable-line no-console
      this.resolveReady()
    }
  }

  private recreate(restoreSnapshot: boolean) {
    const request = indexedDB.deleteDatabase(this.dbName)
    request.onsuccess = () => this.open(restoreSnapshot)
    request.onerror = () => this.resolveReady()
    request.onblocked = () => this.resolveReady()
  }

  private reopen(restoreSnapshot: boolean) {
    this.db = undefined
    this.open(restoreSnapshot)
  }

  /**
   * Rewrites the whole snapshot after a reconnect: the in-memory state is newer
   * than the file, so the roles are reversed compared to `load`. Clearing first
   * drops rows removed while the connection was down.
   */
  private flushSnapshot() {
    const store = this.getStore(true)
    if (!store) {
      return
    }
    store.clear()
    Object.values(this.snapshot).forEach(value => store.put(value))
  }

  private load() {
    const store = this.getStore()
    if (!store) {
      this.resolveReady()
      return
    }
    const request = store.getAll()
    request.onsuccess = () => {
      const next: Record<string, T> = {}
      request.result.forEach((row: T) => {
        next[row[this.keyPath] as string] = row
      })
      this.snapshot = next
      this.entriesCount = request.result.length
      this.resolveReady()
      this.keyListeners.forEach((_, key) => this.notifyKey(key))
      this.notifyGlobal()
    }
    request.onerror = () => this.resolveReady()
  }

  private getStore(write = false) {
    if (!this.db) {
      return
    }
    try {
      return this.db
        .transaction(this.storeName, write ? 'readwrite' : 'readonly')
        .objectStore(this.storeName)
    } catch {
      // `transaction` throws synchronously once the connection is closing, and
      // `close` does not always arrive before that happens.
      this.reopen(true)
      return
    }
  }

  private notifyGlobal() {
    this.globalListeners.forEach(listener => this.safeCall(listener))
  }

  private notifyKey(key: string) {
    this.keyListeners.get(key)?.forEach(listener => this.safeCall(listener))
  }

  private safeCall(listener: CacheListener) {
    try {
      listener()
    } catch (error) {
      console.error(error) // eslint-disable-line no-console
    }
  }
}

export default IndexedDbCache
